/**
 * Content runtime (docs/PERF.md "Per-world content split"). The main bundle carries no lesson content: at start-up the
 * light SHELL (ids, titles, levels, structure, lookups) is loaded into `CONTENT` / `IX`; each world's heavy data
 * (strokes, figures, mini-unit items, story layer) arrives as a separate chunk and is hydrated IN PLACE, so every
 * object reference (IX maps, React props) stays valid. Chunks come from the build (`src/generated/content`, hashed
 * JS chunks, runtime-cached by the service worker) or from a downloaded content pack (lib/contentPack.ts).
 *
 *  - `ensureMiniUnit(id)` / `ensureDrawings(ids)` / `ensureWorld(w)` / `ensureAllContent()` → Promise (idempotent)
 *  - `useContentReady(chunks)` / `useDrawingReady(d)` → boolean + loads what is missing (screens show skeletons)
 *  - `prefetchMiniUnit(id)` (intent: pointerdown / focus on a card), `idlePrefetch()` (current world + next lesson)
 */
import { useEffect, useSyncExternalStore } from 'react';
import {
  COMPLIMENTS,
  LESSON_LEVEL,
  LETTERS,
  SHARED_CHUNK,
  fallbackChain,
  hydrateChunk,
  indexContent,
  isLightDrawing,
  migrateProgressToCurriculum,
  migrateProgressToCurriculumV2,
  registerContentCatalog,
  registerStoryLayer,
  type ChunkId,
  type ContentPack,
  type ContentShell,
  type Drawing,
  type Locale,
  type MiniUnit,
  type PathConfig,
  type PathId,
  type PathPlan,
  type PathPlanV2,
  type ProgressState,
  type WorldChunk,
} from '@sketchling/core';

/* ------------------------------------------------ sources ------------------------------------------------ */

type Load<T> = () => Promise<{ default: T }>;
// Content files are fetched as JSON (hashed asset URLs), not imported as JS modules: a JS module keeps its whole source
// text (MBs per world) in memory for its lifetime, a fetched + parsed JSON body does not.
const URLS = import.meta.glob<string>('../generated/content/**/*.json', { query: '?url', import: 'default', eager: true });
const fetchJson =
  <T,>(url: string): Load<T> =>
  () =>
    fetch(url).then(async (r) => {
      if (!r.ok) throw new Error(`content ${url}: ${r.status}`);
      return { default: (await r.json()) as T };
    });
const FILES: Record<string, Load<unknown>> = Object.fromEntries(Object.entries(URLS).filter(([k]) => !k.includes('/i18n/')).map(([k, u]) => [k, fetchJson(u)]));
const CATALOGS: Record<string, Load<Record<string, string>>> = Object.fromEntries(Object.entries(URLS).filter(([k]) => k.includes('/i18n/')).map(([k, u]) => [k, fetchJson<Record<string, string>>(u)]));
const bundled = <T,>(name: string): Load<T> | undefined => FILES[`../generated/content/${name}.json`] as Load<T> | undefined;

/** Downloaded parts (content pack v2) that replace bundled ones; set by lib/contentPack.ts before `initContent`. */
export interface ContentOverrides {
  shell?: ContentShell;
  chunk?: (id: ChunkId, hash: string) => Promise<WorldChunk | null>;
  catalog?: (locale: Locale, part: string) => Promise<Record<string, string> | null>;
}
let overrides: ContentOverrides = {};
export const setContentOverrides = (o: ContentOverrides) => void (overrides = o);

/* ------------------------------------------------ live pack ------------------------------------------------ */

const emptyPack = (): ContentPack => ({
  version: 0,
  drawings: [],
  miniUnits: [],
  paths: [],
  playground: { categories: [], items: [] } as unknown as ContentPack['playground'],
  challenges: [],
  stickers: [],
  stories: [],
  studio: [],
  skills: [],
  skillJourneys: [],
});

/** The active pack (light until chunks load). Swapped in place: importers keep the same object. */
export const CONTENT: ContentPack = emptyPack();
export const IX = indexContent(CONTENT);
export { COMPLIMENTS, LETTERS };

/** Owning world of every lesson / doodle drawing (core DRAWING_WORLD; card backgrounds, themes outside the map). */
export const DRAWING_WORLD: Record<string, PathId> = {};
/** Owning world of every v2 drawing (incl. new / stub / crossover drawings). */
export const DRAWING_WORLD_V2: Record<string, PathId> = {};
export const DOODLE_OWNERS: Record<string, PathId> = {};
/** World plans in path order (v1 plan + v2 plan). */
export const WORLD_PLANS: { id: PathId; plan: PathPlan; planV2?: PathPlanV2 }[] = [];
/** v1 paths: the v2 migration needs them to know which activities are new. */
export const V1_PATHS: PathConfig[] = [];

let shell: ContentShell | null = null;
/** Hashes of the chunks shipped with the build (a downloaded release may differ). */
const bundledChunkHash = new Map<string, string>();
/** Mini units of the v2 shell (hydration target even while the v1 pack is active: ids overlap). */
const v2Mus = new Map<string, MiniUnit>();
const v2Drawings = new Map<string, Drawing>();
let v2Pack: ContentPack | null = null;
let v1Pack: ContentPack | null = null;

export const contentShell = () => shell;
export const drawingById = (id: string): Drawing | undefined => IX.drawings.get(id);

/* ------------------------------------------------ revision (re-render on hydration) ------------------------------------------------ */

let rev = 0;
const listeners = new Set<() => void>();
function bump() {
  rev++;
  for (const l of [...listeners]) l();
}
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => void listeners.delete(l);
};
/** Re-render when chunks are hydrated / the pack is swapped. */
export const useContentRev = () => useSyncExternalStore(subscribe, () => rev, () => rev);

/* ------------------------------------------------ start-up ------------------------------------------------ */

function applyPack(next: ContentPack) {
  Object.assign(CONTENT, next);
  Object.assign(IX, indexContent(CONTENT));
}

function useShell(s: ContentShell) {
  shell = s;
  v2Mus.clear();
  v2Drawings.clear();
  for (const m of s.pack.miniUnits) v2Mus.set(m.id, m);
  for (const d of s.pack.drawings) v2Drawings.set(d.id, d);
  v2Pack = s.pack;
  v1Pack = null;
  for (const k of Object.keys(DRAWING_WORLD)) delete DRAWING_WORLD[k as keyof typeof DRAWING_WORLD];
  Object.assign(DRAWING_WORLD, s.drawingWorldV1);
  for (const [id, c] of Object.entries(s.drawingChunk)) if (c !== SHARED_CHUNK) DRAWING_WORLD_V2[id] = c as PathId;
  Object.assign(DOODLE_OWNERS, s.doodleOwners);
  Object.assign(LESSON_LEVEL, s.lessonLevel);
  WORLD_PLANS.splice(0, WORLD_PLANS.length, ...s.worlds);
  loaded.clear();
  loading.clear();
  storyLoaded.clear();
}

let ready: Promise<void> | null = null;
/** Load the shell (downloaded pack if valid, else bundled) — awaited before the first render. */
export function initContent(): Promise<void> {
  if (ready) return ready;
  ready = (async () => {
    const s = overrides.shell ?? ((await bundled<ContentShell>('shell')!()).default as ContentShell);
    const hashes = overrides.shell ? (await bundled<{ chunks: Record<string, string> }>('manifest')?.())?.default.chunks : s.chunks;
    for (const [c, h] of Object.entries(hashes ?? {})) if (h) bundledChunkHash.set(c, h);
    useShell(s);
    applyPack(s.pack);
    active = null;
  })();
  return ready;
}
export const contentReady = () => !!shell;

/** Replace the shell at runtime (a downloaded pack applied now). Loaded chunks are re-hydrated on demand. */
export async function replaceShell(s: ContentShell): Promise<void> {
  const wasV2 = active !== false;
  useShell(s);
  if (wasV2) applyPack(s.pack);
  else await setCurriculumAsync(false, true);
  bump();
}

/* ------------------------------------------------ curriculum v1 / v2 (flag `curriculumV2`) ------------------------------------------------ */

let active: boolean | null = null;
export const isCurriculumV2 = () => active !== false;

async function loadV1(): Promise<ContentPack> {
  if (v1Pack) return v1Pack;
  const v1 = (await bundled<Omit<ContentPack, 'drawings'>>('v1')!()).default;
  v1Pack = { ...structuredClone(v1), drawings: shell!.pack.drawings, curriculum: 1 } as ContentPack;
  return v1Pack;
}

/**
 * Swap the active pack for the curriculum flag (in place). Resolves true when something changed (the caller migrates
 * progress and remounts the screens). The v1 structure is fetched the first time it is needed.
 */
export async function setCurriculumAsync(v2: boolean, force = false): Promise<boolean> {
  await initContent();
  if (!force && v2 === active) return false;
  if (v2) applyPack(v2Pack!);
  else applyPack(await loadV1());
  if (!V1_PATHS.length) V1_PATHS.push(...structuredClone((await loadV1()).paths));
  active = v2;
  return true;
}

/** Progress migration for the active curriculum (no-op until the content is in: persisted state is migrated after start-up). */
export function migrateForCurriculum(p: ProgressState): ProgressState {
  if (active === null || !CONTENT.paths.length) return p;
  return active ? migrateProgressToCurriculumV2(p, V1_PATHS, CONTENT.paths) : migrateProgressToCurriculum(p, CONTENT.paths);
}

/* ------------------------------------------------ chunks ------------------------------------------------ */

const loaded = new Set<ChunkId>();
const loading = new Map<ChunkId, Promise<void>>();
let catalogLocale: Locale = 'en';
const catalogsLoaded = new Set<string>();

export const ALL_CHUNKS: ChunkId[] = ['intro', 'animals', 'nature', 'characters', 'anime', 'cuteObjects', SHARED_CHUNK];
export const isChunkLoaded = (c: ChunkId) => loaded.has(c);

async function fetchChunk(id: ChunkId): Promise<WorldChunk> {
  const hash = shell?.chunks[id] ?? '';
  const dl = overrides.chunk ? await overrides.chunk(id, hash).catch(() => null) : null;
  if (dl) return dl;
  const load = bundled<WorldChunk>(`chunk-${id}`);
  if (!load) throw new Error(`unknown content chunk ${id}`);
  return (await load()).default;
}

async function loadCatalog(locale: Locale, part: string): Promise<void> {
  const key = `${locale}|${part}`;
  if (catalogsLoaded.has(key)) return;
  catalogsLoaded.add(key);
  try {
    const dl = overrides.catalog ? await overrides.catalog(locale, part).catch(() => null) : null;
    const entries = dl ?? (await CATALOGS[`../generated/content/i18n/${locale}/c-${part}.json`]?.())?.default;
    if (entries) registerContentCatalog(locale, entries);
  } catch {
    catalogsLoaded.delete(key);
  }
}
const catalogLocales = (l: Locale) => fallbackChain(l).filter((x) => CATALOGS[`../generated/content/i18n/${x}/c-shell.json`] || overrides.catalog);

/** Content catalogs for a locale: the shell part now + the part of every loaded chunk (i18n `loadLocale`). */
export async function loadContentCatalogs(locale: Locale): Promise<void> {
  catalogLocale = locale;
  const locs = catalogLocales(locale);
  await Promise.all(locs.flatMap((l) => [loadCatalog(l, 'shell'), ...[...loaded].map((c) => loadCatalog(l, `chunk-${c}`))]));
}

/* ------------------------------------------------ story layers (separate, small) ------------------------------------------------ */

export const ALL_WORLDS: PathId[] = ['intro', 'animals', 'nature', 'characters', 'anime', 'cuteObjects'];
const storyLoaded = new Set<string>();
const storyLoading = new Map<string, Promise<void>>();
/** A world's story layer (meet sheets, comics, cast) without its drawings: map chapter signs, Residents, storybook. */
export function ensureStories(worlds: Iterable<string>): Promise<void> {
  const todo: Promise<void>[] = [];
  for (const w of new Set(worlds)) {
    if (storyLoaded.has(w) || loaded.has(w as ChunkId)) continue;
    let p = storyLoading.get(w);
    if (!p) {
      p = (async () => {
        await initContent();
        const bundledHash = bundledChunkHash.get(w);
        const want = shell?.chunks[w as ChunkId];
        // A downloaded release changed this world: its story comes with the (downloaded) chunk.
        if (overrides.chunk && want && bundledHash && want !== bundledHash) return ensureChunks([w as ChunkId]);
        const load = bundled<{ hash: string; story: WorldChunk['story'] }>(`story-${w}`);
        if (!load) return;
        const { story } = (await load()).default;
        await Promise.all(catalogLocales(catalogLocale).map((l) => loadCatalog(l, `chunk-${w}`)));
        if (story) registerStoryLayer(story);
        storyLoaded.add(w);
        storyLoading.delete(w);
        bump();
      })();
      p.catch(() => storyLoading.delete(w));
      storyLoading.set(w, p);
    }
    todo.push(p);
  }
  return Promise.all(todo).then(() => undefined);
}
export const storiesReady = (worlds: Iterable<string>) => [...worlds].every((w) => storyLoaded.has(w) || loaded.has(w as ChunkId));
/** True once the worlds' story layers are registered; loads them. */
export function useStoriesReady(worlds: readonly string[] | undefined): boolean {
  useContentRev();
  const key = worlds?.join(',') ?? '';
  const ok = !worlds || storiesReady(worlds);
  useEffect(() => {
    if (worlds && !storiesReady(worlds)) void ensureStories(worlds).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return ok;
}
export const useAllStoriesReady = (on = true) => useStoriesReady(on ? ALL_WORLDS : undefined);

/** Load + hydrate chunks (idempotent). */
export function ensureChunks(ids: Iterable<ChunkId>): Promise<void> {
  const todo: Promise<void>[] = [];
  for (const id of new Set(ids)) {
    if (loaded.has(id)) continue;
    let p = loading.get(id);
    if (!p) {
      p = (async () => {
        await initContent();
        const [chunk] = await Promise.all([fetchChunk(id), ...catalogLocales(catalogLocale).map((l) => loadCatalog(l, `chunk-${id}`))]);
        hydrateChunk(v2Drawings, v2Mus, chunk);
        if (chunk.story) registerStoryLayer(chunk.story);
        loaded.add(id);
        loading.delete(id);
        bump();
      })();
      p.catch(() => loading.delete(id));
      loading.set(id, p);
    }
    todo.push(p);
  }
  return Promise.all(todo).then(() => undefined);
}

const chunkOfDrawing = (id: string): ChunkId[] => {
  const c = shell?.drawingChunk[id];
  return c ? [c, ...(shell?.drawingDeps[id] ?? [])] : [];
};
/** Chunks a mini unit needs (v2: precomputed; v1 / derived units: from the drawings it references). */
export function chunksForMiniUnit(muId: string): ChunkId[] {
  const pre = active !== false ? shell?.deps[muId] : undefined;
  if (pre) return pre;
  const mu = IX.miniUnits.get(muId);
  if (!mu) return [];
  const ids = new Set<string>();
  if (mu.drawingId) ids.add(mu.drawingId);
  const walk = (x: unknown) => {
    if (typeof x === 'string') {
      if (shell?.drawingChunk[x]) ids.add(x);
    } else if (Array.isArray(x)) x.forEach(walk);
    else if (x && typeof x === 'object') Object.values(x).forEach(walk);
  };
  walk(mu.items);
  return chunksForDrawings([...ids]);
}
export function chunksForDrawings(ids: Iterable<string>): ChunkId[] {
  const out = new Set<ChunkId>();
  for (const id of ids) {
    for (const c of chunkOfDrawing(id)) out.add(c);
    const sw = shell?.storyWorld[id];
    if (sw) out.add(sw);
  }
  return [...out];
}

export const ensureMiniUnit = (muId: string) => ensureChunks(chunksForMiniUnit(muId));
export const ensureDrawings = (ids: Iterable<string>) => ensureChunks(chunksForDrawings(ids));
export const ensureWorld = (w: PathId | string) => ensureChunks([w as ChunkId]);
export const ensureAllContent = () => ensureChunks(ALL_CHUNKS);
export const chunksReady = (ids: Iterable<ChunkId>) => [...ids].every((c) => loaded.has(c));
export const miniUnitReady = (muId: string) => chunksReady(chunksForMiniUnit(muId));
/** A drawing whose strokes / figure are in memory. */
export const drawingReady = (d: Drawing | string | undefined) => {
  const x = typeof d === 'string' ? IX.drawings.get(d) : d;
  return !!x && !isLightDrawing(x);
};

/** True once the chunks are hydrated; starts loading them. Re-renders when they arrive. */
export function useContentReady(chunks: readonly ChunkId[] | undefined): boolean {
  useContentRev();
  const key = chunks?.join(',') ?? '';
  const ok = !chunks || chunksReady(chunks);
  useEffect(() => {
    if (chunks && !chunksReady(chunks)) void ensureChunks(chunks).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return ok;
}
export const useMiniUnitReady = (muId: string | undefined) => useContentReady(muId ? chunksForMiniUnit(muId) : undefined);
export const useAllContentReady = (on = true) => useContentReady(on ? ALL_CHUNKS : undefined);
/** A drawing (+ what it is built from) is hydrated; loads it when not. */
export function useDrawingReady(d: Drawing | undefined): boolean {
  useContentRev();
  const light = !!d && isLightDrawing(d);
  useEffect(() => {
    if (d && isLightDrawing(d)) void ensureDrawings([d.id]).catch(() => undefined);
  }, [d, light]);
  return !!d && !light;
}

/* ------------------------------------------------ prefetch ------------------------------------------------ */

/** Intent prefetch (pointerdown / focus on a world or lesson card): starts the download before the tap ends. */
export function prefetchMiniUnit(muId: string | undefined) {
  if (muId) void ensureMiniUnit(muId).catch(() => undefined);
}
export function prefetchWorld(w: string | undefined) {
  if (w) void ensureWorld(w).catch(() => undefined);
}
const idle = (fn: () => void) => {
  const ric = (globalThis as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
  if (ric) ric(fn, { timeout: 4000 });
  else setTimeout(fn, 1200);
};
/** Idle prefetch: the current world + the next lesson's chunks, once the first screen is interactive. */
export function idlePrefetch(world: string | undefined, nextMiniUnit?: string) {
  idle(() => {
    prefetchWorld(world);
    prefetchMiniUnit(nextMiniUnit);
  });
}

/* ------------------------------------------------ world helpers ------------------------------------------------ */

/** A world's own v1 drawings (path + off-path lessons, stubs), in authoring order — light until the chunk loads. */
export const worldDrawings = (w: string): Drawing[] => (shell?.pack.drawings ?? []).filter((d) => DRAWING_WORLD[d.id] === w && !DOODLE_OWNERS[d.id]);
/** The quick doodles a world owns. */
export const worldDoodles = (w: string): Drawing[] => (shell?.pack.drawings ?? []).filter((d) => DOODLE_OWNERS[d.id] === w);

/* ------------------------------------------------ intent + idle prefetch ------------------------------------------------ */

/** What a card's test id points at (the cards already carry stable ids): lesson, playground item, world or drawing. */
function prefetchFor(el: Element | null) {
  for (let n: Element | null = el, depth = 0; n && depth < 6; n = n.parentElement, depth++) {
    const id = n.getAttribute('data-testid');
    if (!id) continue;
    let m: RegExpMatchArray | null;
    if ((m = id.match(/^path-(?:node|new)-(.+)$/))) return prefetchMiniUnit(m[1]);
    if ((m = id.match(/^pg-item-(.+)$/))) return prefetchMiniUnit(IX.playground.get(m[1]!)?.miniUnitId);
    // A world card opens the world's map: it needs the story layer (chapter signs), not the strokes; lessons prefetch
    // their own chunks from the lesson card.
    if ((m = id.match(/^(?:path-card|resident|residents|creature-world)-(.+)$/))) return void ensureStories([m[1]!]).catch(() => undefined);
    if ((m = id.match(/^expo-room-(.+)$/))) return prefetchWorld(m[1]);
    if ((m = id.match(/^(?:creature|expo)-(.+)$/)) && IX.drawings.has(m[1]!)) return void ensureDrawings([m[1]!]).catch(() => undefined);
  }
}

let intentInstalled = false;
/** Pointer-down / keyboard focus on a card starts loading its content before the tap completes (≈ 100-200 ms head start). */
export function installIntentPrefetch() {
  if (intentInstalled || typeof document === 'undefined') return;
  intentInstalled = true;
  const on = (e: Event) => prefetchFor(e.target instanceof Element ? e.target : null);
  document.addEventListener('pointerdown', on, { capture: true, passive: true });
  document.addEventListener('focusin', on, { capture: true, passive: true });
}

/** First open lesson of a world's path for this progress (idle prefetch target). */
export function nextLessonOf(p: ProgressState | undefined, world: PathId): string | undefined {
  const path = IX.paths.get(world);
  if (!path) return undefined;
  for (const u of path.units) for (const i of u.items) if (i.miniUnitId && !p?.completedMiniUnits?.includes(i.miniUnitId)) return i.miniUnitId;
  return undefined;
}

/** Chunks for every drawing id found anywhere in a value (studio flows, challenge lists…). */
export function chunksReferencedBy(x: unknown): ChunkId[] {
  const ids = new Set<string>();
  const walk = (v: unknown) => {
    if (typeof v === 'string') {
      if (shell?.drawingChunk[v]) ids.add(v);
    } else if (Array.isArray(v)) v.forEach(walk);
    else if (v && typeof v === 'object') Object.values(v).forEach(walk);
  };
  walk(x);
  return chunksForDrawings(ids);
}
let studioChunks: ChunkId[] | null = null;
/** My Studio: the worlds its flows compose from (+ the shared parts). */
export const useStudioContentReady = () => useContentReady((studioChunks ??= chunksReferencedBy(CONTENT.studio)));
